/**
 * MASONRY COURSING (2026-10-04, Matt: "For the brick exterior finish material, add linework to
 * the outside visible face of it like we did for vertical wood siding. Make it a common brick
 * size as default but add a couple alternative sizes and bond patterns. I want at least one
 * version of a thin and long brick in a running bond").
 *
 * A wall finish whose CladdingSpec is `profile: 'masonry'` carries `masonry: { unit, bond }`.
 * The finish layer stays flat; bim-geometry finishFaces.ts draws the BED joints (one per
 * course) and the HEAD joints (staggered by the bond) on its visible face, across every
 * finished patch (skins, attach caps, strips, skirts) like the siding joints.
 *
 * Sizes are nominal modules: the brick's face plus one 3/8" joint.
 */
const IN = 1 / 12
const J = 0.375 * IN

export interface MasonryUnitSpec {
  label: string
  /** face length + joint (feet) — the stretcher module along the course */
  lengthFt: number
  /** face height + joint (feet) — one course */
  courseFt: number
  /** depth + joint (feet) — the header module, where a bond turns bricks end-out */
  headerFt: number
}

export const MASONRY_UNITS: Record<string, MasonryUnitSpec> = {
  // the common US brick
  modular: { label: 'Modular — 7-5/8" × 2-1/4"', lengthFt: 7.625 * IN + J, courseFt: 2.25 * IN + J, headerFt: 3.625 * IN + J },
  queen: { label: 'Queen — 7-5/8" × 2-3/4"', lengthFt: 7.625 * IN + J, courseFt: 2.75 * IN + J, headerFt: 3 * IN + J },
  norman: { label: 'Norman — 11-5/8" × 2-1/4"', lengthFt: 11.625 * IN + J, courseFt: 2.25 * IN + J, headerFt: 3.625 * IN + J },
  roman: { label: 'Roman — 11-5/8" × 1-5/8"', lengthFt: 11.625 * IN + J, courseFt: 1.625 * IN + J, headerFt: 3.625 * IN + J },
  // thin + long (Matt: "at least one version of a thin and long brick in a running bond")
  longThin: { label: 'Long thin — 15-5/8" × 1-5/8"', lengthFt: 15.625 * IN + J, courseFt: 1.625 * IN + J, headerFt: 3.625 * IN + J },
}
export const DEFAULT_MASONRY_UNIT = 'modular'

export const MASONRY_BONDS: Record<string, { label: string }> = {
  running: { label: 'Running bond (1/2 offset)' },
  third: { label: 'Third running bond (1/3 offset)' },
  stack: { label: 'Stack bond' },
  flemish: { label: 'Flemish bond (stretcher + header each course)' },
  english: { label: 'English bond (stretcher / header courses)' },
  common: { label: 'Common bond (header course every 6th)' },
}
export const DEFAULT_MASONRY_BOND = 'running'

export function masonryUnitOf(key: string | null | undefined): MasonryUnitSpec {
  return MASONRY_UNITS[key || ''] ?? MASONRY_UNITS[DEFAULT_MASONRY_UNIT]
}

/**
 * The head joints of course `c` (0 = the bottom course): `{ periodFt, offsets }` — the joints
 * sit at every `offset + k * periodFt` along the face.
 */
export function courseHeadJoints(unitKey: string | null | undefined, bondKey: string | null | undefined, c: number): { periodFt: number; offsets: number[] } {
  const u = masonryUnitOf(unitKey)
  const L = u.lengthFt, H = u.headerFt
  const odd = ((c % 2) + 2) % 2 === 1
  switch (MASONRY_BONDS[bondKey || ''] ? bondKey : DEFAULT_MASONRY_BOND) {
    case 'stack': return { periodFt: L, offsets: [0] }
    case 'third': return { periodFt: L, offsets: [((((c % 3) + 3) % 3) * L) / 3] }
    case 'english': return odd ? { periodFt: H, offsets: [H / 2] } : { periodFt: L, offsets: [0] }
    case 'flemish': {
      const P = L + H
      const o = odd ? P / 2 : 0
      return { periodFt: P, offsets: [o, o + L] }
    }
    case 'common': return ((c % 6) + 6) % 6 === 5 ? { periodFt: H, offsets: [H / 2] } : { periodFt: L, offsets: [odd ? L / 2 : 0] }
    default: return { periodFt: L, offsets: [odd ? L / 2 : 0] }
  }
}

/**
 * STONE PATTERNS (2026-10-05, Matt: "explore the idea of stones in different patterns" after
 * brick). A masonry finish with `masonry.pattern` lays STONES instead of bricks — the unit and
 * bond are then unused. Every pattern is solid-color 3D stones in shades of the finish's own
 * color over the mortar (bim-geometry stone3d.ts), laid from one deterministic layout that both
 * the 3D stones and the elevation outlines read.
 *
 * Sizes are the stones' faces; `jointFt` is the mortar joint between them; `proudFt` how far a
 * stone's face stands off the mortar (a range = each stone varies, for a rustic read).
 */
export interface StonePatternSpec {
  label: string
  jointFt: number
  /** stone face standing off the mortar: [min, max] */
  proudFt: [number, number]
  /** the bevel round each stone's face (a dressed edge), as a share of its proud depth */
  bevel: number
  /** how far apart the shades step: 1 = brick's spread */
  shadeSpread: number
}

export const STONE_PATTERNS: Record<string, StonePatternSpec> = {
  // squared stones in level courses; the course heights vary course to course, the lengths
  // stone to stone
  ashlar: { label: 'Coursed ashlar', jointFt: 0.375 * IN, proudFt: [0.375 * IN, 0.5 * IN], bevel: 0.5, shadeSpread: 1.2 },
  // squared stones of two or three heights breaking the courses (random rectangular)
  randomAshlar: { label: 'Random ashlar', jointFt: 0.5 * IN, proudFt: [0.375 * IN, 0.75 * IN], bevel: 0.6, shadeSpread: 1.4 },
  // long thin stones stacked tight, each set a little in or out
  ledgestone: { label: 'Ledgestone (stacked)', jointFt: 0.125 * IN, proudFt: [0.375 * IN, 1.25 * IN], bevel: 0.25, shadeSpread: 1.6 },
}

export function stonePatternOf(key: string | null | undefined): StonePatternSpec | null {
  return (key && STONE_PATTERNS[key]) || null
}
